import { Box, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import { DragEvent, ReactNode, useRef } from 'react';
import { Upload } from 'react-feather';
import IconFeather from 'src/components/common/Icon';
import { MAX_SIZE_FILE_UPLOAD_1M } from 'src/utils/constants/config';
import useSnackbarStore from 'src/zustand/useSnackbarStore';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface IProps {
  description?: ReactNode;
  accept?: string;
  title?: string;
  onChange: (files: File) => void;
  limit?: number;
  type: 'image' | 'file';
}

const defaultDescription = 'Only support .xlsx, .csv, .pdf, .txt, and .xml files';
const defaultAccept = '.xlsx,.csv,.pdf,.txt,.xml';
const defaultTitle = 'Drag & Drop your File(s) or';

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DragAndDropFileBox = ({
  description = defaultDescription,
  accept = defaultAccept,
  onChange,
  limit = MAX_SIZE_FILE_UPLOAD_1M,
  title = defaultTitle,
  type,
}: IProps) => {
  const inputRef = useRef(null as HTMLInputElement | null);
  const { dispatch } = useSnackbarStore();
  const {
    palette: {
      background: { paper1 },
      divider,
    },
    shape: { borderRadius },
  } = useTheme();

  const validateFileType = (file: File) => {
    switch (type) {
      case 'image':
        return validateImage(file);
      case 'file':
        return validateFile(file);
      default:
        return false;
    }
  };
  const validateFile = (file: File) => {
    const conversion = accept.split(',');
    conversion.forEach((fileType) => {
      if (file.name.endsWith(fileType)) return true;
    });
    return false;
  };

  const validateImage = (file: File) => {
    if (accept === 'image/*') return file.type.startsWith('image/');
    const conversion = accept.split(',');
    conversion.forEach((fileType) => {
      if (file.name.endsWith(fileType)) return true;
    });
    return false;
  };

  const handleClickBrowse = () => {
    if (!inputRef.current) return;
    inputRef.current.click();
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleChangeFile(file);
  };

  const handleChangeFile = (file: File) => {
    if (!file) return;

    const isValidFileSize = file.size <= limit;
    const isValidType = validateFileType(file);

    if (!isValidFileSize) {
      dispatch({
        title: 'Error',
        description: `File size exceeds ${formatFileSize(file.size)} limit. Please choose a smaller file.`,
        open: true,
        color: 'error',
        severity: 'error',
      });
      return;
    }
    if (!isValidType) {
      dispatch({
        title: 'Error',
        description: `Please upload an ${accept} file.`,
        open: true,
        color: 'error',
        severity: 'error',
      });
      return;
    }
    onChange(file);
  };

  return (
    <Box
      component={'div'}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      border={`1px dashed ${divider}`}
      borderRadius={borderRadius / 2}
      bgcolor={paper1}
      p={2}
    >
      <Stack alignItems={'center'} spacing={1}>
        <IconButton size="large" color="primary">
          <IconFeather icon={<Upload />} />
        </IconButton>
        <Typography display={'inline'} variant="subtitle1" color={'text.primary'}>
          {title}{' '}
          <Typography
            component={'span'}
            onClick={handleClickBrowse}
            sx={{ cursor: 'pointer' }}
            color={'secondary.main'}
            display={'inline'}
          >
            Browse
          </Typography>
        </Typography>
        <Typography variant="caption" color={'textTertiary.main'}>
          {description}
        </Typography>
      </Stack>

      <VisuallyHiddenInput
        ref={inputRef}
        type="file"
        onChange={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.target.files) {
            const file = event.target.files[0];
            handleChangeFile(file);
          }
        }}
        accept={accept}
        multiple
      />
    </Box>
  );
};

export default DragAndDropFileBox;
